/**
 * PlanStatusCard — displays current plan, coin balance, and upgrade/manage actions.
 *
 * Shows:
 * - Plan badge (Free / Weekend Pass / Monthly)
 * - Coin balance
 * - Period end date
 * - Upgrade button (opens PaywallModal) for free plans and expired access
 * - "Zarządzaj płatnościami" button (Stripe Portal) for recurring paid plans
 */

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PaywallModal, PlanOption } from "@/components/high/billing/PaywallModal"
import { getBillingPortalUrl } from "@/services/api"
import { toast } from "sonner"

interface PlanStatusCardProps {
  planName: string
  planCode: string
  balance: number
  periodEnd: string | null
  isPaid: boolean
  hasRunningAccess: boolean
  canManagePayments: boolean
  upgradePlans: PlanOption[]
}

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString("pl-PL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  } catch {
    return iso
  }
}

export function PlanStatusCard({
  planName,
  planCode,
  balance,
  periodEnd,
  isPaid,
  hasRunningAccess,
  canManagePayments,
  upgradePlans,
}: PlanStatusCardProps) {
  const { t } = useTranslation()
  const [paywallOpen, setPaywallOpen] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)

  const handleManagePayments = async () => {
    setPortalLoading(true)
    try {
      const { portal_url } = await getBillingPortalUrl()
      window.open(portal_url, "_blank")
    } catch {
      toast.error(t("billing.portalError"))
    } finally {
      setPortalLoading(false)
    }
  }

  const planBadgeVariant = isPaid ? "default" : "outline"

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg">{t("billing.myPlan")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Plan badge */}
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground text-sm">{t("billing.currentPlan")}</span>
            <Badge
              variant={planBadgeVariant}
              className={
                isPaid
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground"
              }
            >
              {planName}
            </Badge>
          </div>

          {/* Coin balance */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t("billing.coinBalance")}</span>
            <span className="text-foreground font-bold">
              {t("billing.coinsAmount", { count: balance })}
            </span>
          </div>

          <Link
            to="/profile/coins"
            className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            {t("billing.coinHistoryLink")}
          </Link>

          {/* Period end */}
          {periodEnd ? (
            <p className="text-sm text-muted-foreground">
              {t("billing.coinsValidUntil")}{" "}
              <span className="text-foreground">{formatDate(periodEnd)}</span>
            </p>
          ) : planCode === "free" ? (
            <p className="text-sm text-muted-foreground">{t("billing.freeCoinsRenewDaily")}</p>
          ) : null}

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            {(!isPaid || !hasRunningAccess) && (
              <Button
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => setPaywallOpen(true)}
              >
                {t('billing.upgrade')}
              </Button>
            )}
            {canManagePayments && (
              <Button
                variant="outline"
                className="w-full border-border text-foreground hover:bg-accent"
                disabled={portalLoading}
                onClick={handleManagePayments}
              >
                {portalLoading ? t("common.loading") : t("billing.managePayments")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        trigger="insufficient_coins"
        plans={upgradePlans}
        message={t("billing.choosePlan")}
      />
    </>
  )
}
